import type {HeadersArgs, LoaderFunctionArgs, MetaFunction} from 'react-router'
import {useLoaderData} from 'react-router'

import {mergeMeta} from '@core/lib/mergeMeta'
import {json} from '@core/lib/jsonResponse'
import {getSession} from '@core/auth/session.server'
import {getProductId} from '@core/products/registry'
import {logger} from '@core/utils/logger.server'
import {readWallPage} from '@core/wall/tiles.server'
import {leanOfSession} from '@core/wall/touches.server'
import type {FacetOffer} from '@core/wall/facetOffer'
import {parseProfileKey} from '@core/wall/profile'
import {TOILE_FACETS} from '@product/toile/wall/facets'
import {readMine} from '@core/wall/mine.server'
import {adoptVisitorTakes} from '@core/wall/adopt.server'
import type {OwnerRef} from '@core/wall/owner.server'
import {toTileSummary} from '@core/wall/takes.server'
import {readWallQueryParam} from '@core/wall/tsQuery'
import type {MineResponse} from '@core/wall/contract'
import {Wall} from '@core/wall/ui/Wall'
import type {PackIndexItem, SeriesGroup} from '@core/packs/series/types'
import {TosConsentCard} from '@core/consent/TosConsentCard'
import {Header} from '@product/toile/components/Header/Header'
import {SignInLayer} from '@core/auth-ui/SignInLayer/SignInLayer'
import {toileSignInCardFor} from '@product/toile/wall/signInCard'
import {HomeDoorstep} from '@core/wall/home/HomeDoorstep'
import {homeMeta} from '@core/wall/home/doorstep'
import {livePacks} from '@product/toile/packs/registry'
import {toileHome, toileHouse} from '@product/toile/wall/home'
import {toileTheme} from '@product/toile/wall/theme'
import {toileHouse as toileWallHouse} from '@product/toile/wall/house.server'
import consentStyles from '@product/toile/wall/TosConsentCard.module.scss'
import {TOILE_CONSENT_COPY} from '@product/toile/wall/consentCopy'

// The root's `config.meta` (`products/toile/config.ts`) supplies the bare "toile" title every OTHER
// route falls back to; overriding here, via `mergeMeta`, touches only this document.
export const meta: MetaFunction = ({matches}) => mergeMeta(matches, homeMeta(toileHouse))

/**
 * A `?q=` page now SSRs a real searched render, so it states its own caching (re-review N2); the
 * anonymous bare wall keeps the shipped default (no header, CachingDisabled at CloudFront's
 * `default_cache_behavior`) — this fix does not touch what it does not have to.
 *
 * Anonymous, a searched page is a pure function of its URL exactly as `GET /wall` is, and takes
 * that route's rule verbatim (`wall.ts:54`). Signed in, the document also carries the user's own
 * rows (`mine`) whether or not `q` is set — this takes the rule every signed-in wall route follows
 * (`routeHelpers.server.ts`'s `NO_STORE_HEADERS`) unconditionally on `signedIn`, not only on the
 * `?q=` branch (error-handling review F10: the signed-in bare wall used to emit no header at all,
 * resting the same correctness property — one user's `mine` never reaching a shared cache — on
 * CloudFront's default behaviour in another repository directory rather than on the response
 * itself). A shared cache must never hand one user's arrival block to the next.
 *
 * A visitor holding free-pick rows (`mine` present) is the same case: their document carries their own
 * pictures. A visitor with none keeps the anonymous rule above.
 */
function searchPageHeaders({q, private: isPrivate}: {q: string; private: boolean}): HeadersInit | undefined {
    if (isPrivate) return {'Cache-Control': 'private, no-store'}
    if (!q) return undefined
    return {'Cache-Control': 'public, max-age=60, s-maxage=60'}
}

/**
 * Full-catalog series index (pack-index, 2026-08-30) for the endpaper's `PackIndex` — the same
 * `groupPacksBySeries(cards, TOILE_SERIES)` call `_layout.packs.tsx`'s loader makes. Factored to
 * its own function only so `loader()`'s `Promise.all` reads as one call per concern.
 */
async function loadPackIndexGroups(): Promise<SeriesGroup<PackIndexItem>[]> {
    const {groupPacksBySeries} = await import('@core/packs/series/classify.server')
    const {TOILE_SERIES} = await import('@product/toile/packs/series.server')
    // Slim to {slug, name} BEFORE grouping, as narcis's homepage does (`_layout._index.tsx:353`):
    // the groups travel in the loader payload, and full ToilePack entries (descriptions, cells)
    // put 1.8MB of pack copy in every anonymous document (measured 2026-09-01).
    return groupPacksBySeries(livePacks.map((p) => ({slug: p.slug, name: p.name})), TOILE_SERIES)
}

/**
 * The wall — `toile.pictures/`, wall plan d (`thinking/plans/2026_08_31_wall_plan_d_wall_ui.md` §
 * Files): "becomes the wall route: loader = first page server-side (`readWallPage`, so the first
 * paint is SSR) + `mine` when signed in; meta unchanged." Replaces the earlier static hero-wall +
 * pack-catalog + FAQ home (BRIEF §2: "The wall is the whole house" — it replaces home and the hub).
 */
export async function loader({request}: LoaderFunctionArgs) {
    const product = getProductId()
    const url = new URL(request.url)
    // The searched page is SSR'd, not the default wall (re-review N2): a `?q=` deep link — shared,
    // bookmarked, or a search engine's own copy of a query URL — used to render the whole catalog
    // by `default_rank` under a field already saying "koi", with the ghost stranded at the end and
    // `wall_search` reporting `matched_count: 0`. `readWallQueryParam` is the same reading `GET
    // /wall` does, so the SSR page, the client's field and the first client fetch all agree.
    const q = readWallQueryParam(url.searchParams)
    const session = await getSession(request.headers.get('cookie'))
    const userId = session.get('userId') as string | undefined
    const dbSessionId = session.get('dbSessionId') as string | undefined
    // Whose rows the document carries: the member's, or - the house lets a signed-out first pick write - the
    // visitor's own (plan `2026_10_25_toile_first_take_free.md` § 2). A visitor with none gets `mine: null`.
    const owner: OwnerRef | null = userId
        ? {kind: 'user', userId}
        : toileWallHouse.freePick && dbSessionId ? {kind: 'visitor', sessionId: dbSessionId} : null

    let wallPage: Awaited<ReturnType<typeof readWallPage>>
    let mineCore: Awaited<ReturnType<typeof readMine>> | null
    let packIndexGroups: SeriesGroup<PackIndexItem>[]
    let profile = ''
    let personal = false
    let facetOffer: FacetOffer = {}
    try {
        // A sign-in's landing document must show the rows it just adopted, a rendering one included.
        if (userId && dbSessionId) await adoptVisitorTakes(dbSessionId, userId)
        ;({profile, personal, facetOffer} = await leanOfSession(session, product, TOILE_FACETS, new Date()))
        ;[wallPage, mineCore, packIndexGroups] = await Promise.all([
            readWallPage({product, q, cursor: null, profile: parseProfileKey(profile)}),
            owner ? readMine({product, owner, q}) : Promise.resolve(null),
            // The endpaper's full-catalog index — computed only when anonymous, the doorstep's own
            // gate, so a signed-in request never pays for the classification pass. Dynamic import:
            // `series.server.ts` and `classify.server.ts` are server-only.
            userId ? Promise.resolve<SeriesGroup<PackIndexItem>[]>([]) : loadPackIndexGroups(),
        ])
    } catch (err) {
        // Error-handling review F1: the wall's own SSR loader was the one uncaught read left in this
        // tree, and the one that matters most — it is the product's only page, what a search crawler
        // fetches, and an uncaught throw here used to hand the whole homepage to `root.tsx`'s generic
        // ErrorBoundary with no log line naming the wall. Degrades to an empty first page with
        // `mine: null` rather than losing the document: the wall component already renders that
        // state correctly (a fresh anonymous visit is exactly `initialMine: null`, an empty
        // `wallPage.tiles`). `no-store` — a degraded document must never enter a shared cache.
        logger.error('[wall] SSR loader failed', {
            q, userId,
            error: err,
        })
        return json({
            wallPage: {tiles: [], next_cursor: null, seam: false},
            profile: '',
            facetOffer: {} as FacetOffer,
            mine: null,
            signedIn: !!userId,
            searched: !!q,
            packIndexGroups: [],
        }, {headers: {'Cache-Control': 'no-store'}})
    }

    // A visitor with neither a done nor a pending row has nothing of their own to carry.
    const hasRows = !!mineCore && (userId !== undefined || mineCore.done.length > 0 || mineCore.pending.length > 0)
    const mine: MineResponse | null = mineCore && hasRows ? {...mineCore, identity: {kind: 'none'}} : null

    return json({
        wallPage: {
            tiles: wallPage.tiles.map(toTileSummary),
            next_cursor: wallPage.nextCursor,
            seam: wallPage.seam,
            ...(wallPage.matchedCount !== undefined ? {matched_count: wallPage.matchedCount} : {}),
        },
        mine,
        signedIn: !!userId,
        searched: !!q,
        packIndexGroups,
        profile,
        facetOffer,
    }, {headers: searchPageHeaders({q, private: !!userId || mine !== null || personal})})
}

/**
 * React Router 7 propagates only `Set-Cookie` from a loader's own `Response` onto the DOCUMENT
 * response (`getDocumentHeadersImpl`, `react-router/dist/.../index.js:782` — every other header
 * needs a `headers` export). This forwards what the loader decided above, and nothing else.
 */
export function headers({loaderHeaders}: HeadersArgs) {
    const cacheControl = loaderHeaders.get('Cache-Control')
    return cacheControl ? {'Cache-Control': cacheControl} : {}
}

export default function Home() {
    const {wallPage, mine, signedIn, searched, packIndexGroups, profile, facetOffer} = useLoaderData<typeof loader>()

    return (
        <>
            <Wall
                theme={toileTheme}
                initialWallPage={wallPage}
                profile={profile}
                facetOffer={facetOffer}
                initialMine={mine}
                signedIn={signedIn}
                renderHeader={(search) => <Header search={search} />}
                renderSignIn={({redirectTo, trigger, hasPicture, onClose}) => (
                    <SignInLayer redirectTo={redirectTo} trigger={trigger} onClose={onClose} card={toileSignInCardFor({trigger, hasPicture})} />
                )}
                renderConsentCard={({checked, onTick, focusToken}) => (
                    <TosConsentCard
                        styles={consentStyles}
                        checked={checked}
                        onTick={onTick}
                        focusToken={focusToken}
                        termsHref="/terms"
                        privacyHref="/privacy"
                        copy={TOILE_CONSENT_COPY}
                    />
                )}
            />

            {/* A `?q=` page canonicalises to `/`: its tiles are not the home's gallery. */}
            <HomeDoorstep house={toileHome(packIndexGroups)} anon={!signedIn} gallery={searched ? [] : wallPage.tiles} />
        </>
    )
}
